<template>
  <div
    class="app-pagination"
    data-testid="app-pagination"
  >
    <template v-if="onlyOnePage">
      <span>Showing </span>
      <strong>{{ pagination.current_count || pagination.total_count }}</strong>
      <span> of</span>
      <strong> {{ pagination.current_count || pagination.total_count }}</strong>
      <span> {{ pagination.total_count | pluralize(resource.one, resource.many) }}</span>
    </template>
    <template v-else>
      <div
        class="pagination-container resources-pagination"
        data-testid="pagination-container"
      >
        <a
          v-if="!hideFirstLastPageButtons"
          v-tooltip="tooltipMessage"
          class="btn btn-default btn-pagination btn-pagination-start pull-left"
          :disabled="pagination.first_page"
          href=""
          data-testid="pagination-first"
          @click.prevent="!pagination.first_page && onPageClick(1)"
        >
          <i class="hi hi-double-left hi-12" />
        </a>
        <a
          v-tooltip="tooltipMessage"
          class="btn btn-default btn-pagination pull-left"
          :class="{ disabled: loading }"
          :disabled="pagination.first_page"
          href=""
          data-testid="pagination-prev"
          @click.prevent="!pagination.first_page && onPageClick(pagination.prev_page)"
        >
          <i class="hi hi-left hi-12" />
        </a>
        <a
          v-if="!hideFirstLastPageButtons"
          v-tooltip="tooltipMessage"
          class="btn btn-default btn-pagination btn-pagination-end pull-right"
          :disabled="pagination.last_page"
          href=""
          data-testid="pagination-last"
          @click.prevent="!pagination.last_page && onPageClick(totalPages)"
        >
          <i class="hi hi-double-right hi-12" />
        </a>
        <a
          v-tooltip="tooltipMessage"
          class="btn btn-default btn-pagination pull-right"
          :class="{ disabled: loading }"
          :disabled="pagination.last_page"
          href=""
          data-testid="pagination-next"
          @click.prevent="!pagination.last_page && onPageClick(pagination.next_page)"
        >
          <i class="hi hi-right hi-12" />
        </a>
        <span class="pages">
          <!-- FIRST PAGE IS ALWAYS RENDERED -->
          <a
            v-tooltip="tooltipMessage"
            class="paginate-link"
            :class="{ 'is-current': isActive(1) }"
            :data-testid="'pagination-page-1'"
            @click.prevent="!isActive(1) && onPageClick(1)"
          >
            1
          </a>

          <!-- WITHIN OUTER RANGE (1 2 3 4 ... 12 13 14 15) -->
          <template v-if="minimumNumberOfPages && withinOuterBounds">
            <a
              v-for="page in range(2, outerRange + 1)"
              :key="page"
              v-tooltip="tooltipMessage"
              class="paginate-link"
              :class="{ 'is-current': isActive(page) }"
              :data-testid="`pagination-page-${page}`"
              @click.prevent="!isActive(page) && onPageClick(page)"
            >
              {{ page }}
            </a>
            <span class="pagination-ellipsis">&hellip;</span>
            <a
              v-for="page in range(totalPages - outerRange, totalPages - 1)"
              :key="page"
              v-tooltip="tooltipMessage"
              class="paginate-link"
              :class="{ 'is-current': isActive(page) }"
              :data-testid="`pagination-page-${page}`"
              @click.prevent="!isActive(page) && onPageClick(page)"
            >
              {{ page }}
            </a>
          </template>
          <!-- OUTSIDE OUTER RANGE (1 ... 3 4 5 6 7 ... 15) -->
          <template v-else-if="minimumNumberOfPages && !withinOuterBounds">
            <span
              v-if="currentPage - innerRange > 2"
              class="pagination-ellipsis"
            >
              &hellip;
            </span>
            <a
              v-for="page in range(currentPage - innerRange, currentPage + innerRange)"
              :key="page"
              v-tooltip="tooltipMessage"
              class="paginate-link"
              :class="{ 'is-current': isActive(page) }"
              :data-testid="`pagination-page-${page}`"
              @click.prevent="!isActive(page) && onPageClick(page)"
            >
              {{ page }}
            </a>
            <span
              v-if="currentPage + innerRange + 1 != totalPages"
              class="pagination-ellipsis"
            >
              &hellip;
            </span>
          </template>
          <!-- NORMAL RANGE (1 2 3 4 5 6 8) -->
          <template v-else>
            <a
              v-for="page in range(2, totalPages - 1)"
              :key="page"
              v-tooltip="tooltipMessage"
              class="paginate-link"
              :class="{ 'is-current': isActive(page) }"
              :data-testid="`pagination-page-${page}`"
              @click.prevent="!isActive(page) && onPageClick(page)"
            >
              {{ page }}
            </a>
          </template>

          <!-- LAST PAGE IS ALWAYS RENDERED -->
          <a
            v-tooltip="tooltipMessage"
            class="paginate-link"
            :class="{ 'is-current': isActive(totalPages) }"
            :data-testid="`pagination-page-${totalPages}`"
            @click.prevent="!isActive(totalPages) && onPageClick(totalPages)"
          >
            {{ totalPages }}
          </a>
        </span>
        <div
          v-if="!hidePaginationShowingText"
          class="pagination-showing"
          data-testid="pagination-showing"
        >
          <span>Showing </span>
          <strong class="starting-offset">{{ startingOffset }}</strong>
          <template v-if="withOffset">
            <strong> -</strong>
            <strong class="ending-offset"> {{ endingOffset }}</strong>
          </template>
          <span> of</span>
          <strong> {{ pagination.total_count }}</strong>
          <span> {{ pagination.total_count | pluralize(resource.one, resource.many) }}</span>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { range } from 'lodash-es';

export default {
  name: 'AppPagination',
  props: {
    pagination: {
      type: Object,
      required: true,
      default: () => ({
        current_page: 1,
        total_pages: 1,
        next_page: null,
        prev_page: null,
        limit_value: 20,
        current_count: 1,
        total_count: 1,
        first_page: true,
        last_page: true,
      }),
    },
    resource: {
      type: Object,
      required: true,
      default: () => ({
        one: 'Item',
        many: 'Items',
      }),
    },
    hidePaginationShowingText: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    innerRange: {
      type: Number,
      default: 2,
    },
    outerRange: {
      type: Number,
      default: 3,
    },
    hideFirstLastPageButtons: {
      type: Boolean,
      default: true,
    },
    tooltipTitle: {
      type: String,
      default: '',
    },
  },
  data: () => ({
    firstPage: 1,
  }),
  computed: {
    tooltipMessage() {
      if (!this.tooltipTitle) {
        return null;
      }

      return {
        title: this.tooltipTitle,
        placement: 'bottom',
      };
    },
    currentPage() {
      return this.pagination.current_page;
    },
    previousPage() {
      return this.currentPage - 1;
    },
    totalPages() {
      return this.pagination.total_pages;
    },
    minimumNumberOfPages() {
      return this.totalPages > this.outerRange * 2 + 2;
    },
    inLowerOuterRange() {
      return this.currentPage <= this.outerRange;
    },
    inUpperOuterRange() {
      return this.currentPage > this.totalPages - this.outerRange;
    },
    withinOuterBounds() {
      return this.inLowerOuterRange || this.inUpperOuterRange;
    },
    startingOffset() {
      return this.previousPage * this.pagination.limit_value + 1;
    },
    withOffset() {
      return this.startingOffset !== this.pagination.total_count;
    },
    endingOffset() {
      return this.previousPage * this.pagination.limit_value + this.pagination.current_count;
    },
    onlyOnePage() {
      return this.totalPages === 1;
    },
  },
  methods: {
    range(lowerBound, upperBound) {
      // Lodash's `range` function doesn't include the upper bound; so we have to force it by adding 1
      return range(lowerBound, upperBound + 1);
    },
    isActive(pageNumber) {
      return pageNumber === this.currentPage;
    },
    onPageClick(pageNumber) {
      this.$emit('page-changed', pageNumber);
    },
  },
};
</script>
